import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { AlertBanner } from "@/components/we360/AlertBanner";
import { CollapsibleCard } from "@/pages/Dashboard/CollapsibleCard";
import { LoadingScreen } from "./LoadingScreen";
import { fetchAIRecommendations } from "@/services/recommendations";
import {
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Activity,
  Apple,
  Brain,
  BookOpen,
  CalendarCheck,
  RefreshCw,
  Sparkles,
  Target,
  Flame,
  Sun,
  Moon,
  Lightbulb,
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────
const RISK_COLORS = {
  bajo: "#157346",
  moderado: "#F9A01B",
  alto: "#C0453A",
  critico: "#C0453A",
};

function riskColor(level) {
  if (!level) return "#71644C";
  return RISK_COLORS[String(level).toLowerCase()] || "#71644C";
}

// ─── Main page ────────────────────────────────────────────
export default function Recommendations() {
  const { profile, setProfile } = useUserProfile();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inflight = useRef(false);
  const abortRef = useRef(null);

  const hasAll =
    !!profile.completedAt &&
    !!profile.f2Results?.completedAt &&
    !!profile.f3Results?.completedAt &&
    !!profile.f4Results?.completedAt &&
    !!profile.f5Results?.completedAt;

  const cached = profile.recommendations?.data;
  const generatedAt = profile.recommendations?.generatedAt;

  const isMountedRef = useRef(true);
  const generate = useCallback(async () => {
    if (inflight.current) return;
    if (!profile.email) {
      setError("No se encontró el email del usuario para llamar al servicio IA.");
      return;
    }
    inflight.current = true;
    abortRef.current = new AbortController();
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAIRecommendations(
        profile.email,
        abortRef.current.signal
      );
      if (isMountedRef.current) {
        setProfile((prev) => ({
          ...prev,
          recommendations: { data, generatedAt: new Date().toISOString() },
        }));
      }
    } catch (e) {
      if (!isMountedRef.current) return;
      if (e?.name === "AbortError") {
        // user cancelled — show retry UI instead of looping
        setError("Solicitud cancelada. Puedes volver a intentarlo cuando quieras.");
      } else {
        console.error("[Recommendations] error:", e);
        setError(e.message || "No se pudo obtener tu plan personalizado.");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        inflight.current = false;
        abortRef.current = null;
      }
    }
  }, [profile.email, setProfile]);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort("user-cancel");
    }
  }, []);

  // Auto-fetch on first visit (when forms complete and no cache)
  useEffect(() => {
    if (hasAll && !cached && !loading && !error && !inflight.current) {
      generate();
    }
  }, [hasAll, cached, loading, error, generate]);

  // Cleanup on unmount: cancel any in-flight request
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortRef.current) abortRef.current.abort("user-cancel");
    };
  }, []);

  // ─── No data yet ─────────────────────────────────────────
  if (!hasAll) {
    const pending = [];
    if (!profile.completedAt)
      pending.push({ n: 1, t: "Demográfico y Clínico", p: "/f1" });
    if (!profile.f2Results?.completedAt)
      pending.push({ n: 2, t: "Estado Físico", p: "/f2" });
    if (!profile.f3Results?.completedAt)
      pending.push({ n: 3, t: "Actividad Física", p: "/f3" });
    if (!profile.f4Results?.completedAt)
      pending.push({ n: 4, t: "Nutrición", p: "/f4" });
    if (!profile.f5Results?.completedAt)
      pending.push({ n: 5, t: "Bienestar Emocional", p: "/f5" });
    return (
      <div
        className="min-h-screen font-sans px-3 py-4"
        style={{ background: "#FCFAEE" }}
      >
        <div className="w-full max-w-[420px] mx-auto">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-1 text-xs font-semibold mb-4 cursor-pointer"
            style={{ color: "#00628F", background: "none", border: "none" }}
            data-testid="rec-back-btn"
          >
            <ArrowLeft size={14} /> Volver al dashboard
          </button>
          <div className="we-card p-6 text-center">
            <Sparkles size={32} color="#B5A998" className="mx-auto mb-3" />
            <h2 className="text-base font-bold mb-2" style={{ color: "#2D2A24" }}>
              Completa todas tus evaluaciones
            </h2>
            <p className="text-xs mb-4" style={{ color: "#71644C" }}>
              Para recibir recomendaciones personalizadas necesitas completar
              los 5 formularios.
            </p>
            <div className="space-y-2">
              {pending.map((f) => (
                <div
                  key={f.n}
                  className="flex items-center justify-between p-2.5 rounded-xl cursor-pointer"
                  style={{ background: "#FCFAEE", border: "1px solid #E2D9CB" }}
                  onClick={() => navigate(f.p)}
                >
                  <span
                    className="text-xs font-semibold"
                    style={{ color: "#71644C" }}
                  >
                    F{f.n}: {f.t}
                  </span>
                  <span
                    className="text-[10px] font-semibold"
                    style={{ color: "#00628F" }}
                  >
                    Ir a completar →
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingScreen onCancel={cancel} />;

  // ─── Error state ─────────────────────────────────────────
  if (error && !cached) {
    return (
      <div
        className="min-h-screen font-sans px-3 py-4"
        style={{ background: "#FCFAEE" }}
      >
        <div className="w-full max-w-[420px] mx-auto">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-1 text-xs font-semibold mb-4 cursor-pointer"
            style={{ color: "#00628F", background: "none", border: "none" }}
            data-testid="rec-back-btn"
          >
            <ArrowLeft size={14} /> Volver al dashboard
          </button>
          <div className="we-card p-6 text-center" data-testid="rec-error">
            <AlertTriangle size={32} color="#C0453A" className="mx-auto mb-3" />
            <h2 className="text-base font-bold mb-2" style={{ color: "#2D2A24" }}>
              No pudimos generar tu plan
            </h2>
            <p
              className="text-xs mb-4 leading-relaxed"
              style={{ color: "#71644C" }}
            >
              {error}
            </p>
            <button
              onClick={generate}
              className="cursor-pointer"
              style={{
                padding: "10px 24px",
                borderRadius: 12,
                border: "none",
                background: "#00628F",
                color: "white",
                fontSize: 13,
                fontWeight: 700,
              }}
              data-testid="rec-retry-btn"
            >
              <RefreshCw size={12} className="inline mr-1" /> Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!cached) return <LoadingScreen />;

  const r = cached;
  const summary = r.summary || {};
  const components = r.components || {};
  const physical = components.physical;
  const nutrition = components.nutrition;
  const emotional = components.emotional;

  const riskLevel = summary?.alertas_y_riesgos?.nivel_alerta || "";

  return (
    <div
      className="min-h-screen font-sans px-3 py-4"
      style={{ background: "#FCFAEE" }}
    >
      <div className="w-full max-w-[420px] mx-auto">
        {/* Top nav */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-1 text-xs font-semibold cursor-pointer"
            style={{ color: "#00628F", background: "none", border: "none" }}
            data-testid="rec-back-btn"
          >
            <ArrowLeft size={14} /> Dashboard
          </button>
          <button
            onClick={generate}
            className="flex items-center gap-1 text-[10px] font-semibold cursor-pointer px-2.5 py-1 rounded-lg"
            style={{ color: "#00628F", background: "#E3F0F5", border: "none" }}
            data-testid="rec-regenerate-btn"
          >
            <RefreshCw size={10} /> Regenerar
          </button>
        </div>

        {/* Header */}
        <h1
          className="text-lg font-bold mb-1"
          style={{ color: "#2D2A24" }}
          data-testid="rec-title"
        >
          Tu Plan Personalizado de Salud
        </h1>
        <p className="text-xs mb-4" style={{ color: "#71644C" }}>
          Generado por IA con base en tus 5 evaluaciones
        </p>

        {/* ═══ Executive Summary ═══ */}
        {summary.resumen_ejecutivo && (
          <div
            className="rounded-2xl p-4 mb-3"
            style={{
              background:
                "linear-gradient(135deg, #00628F 0%, #0080A0 50%, #2A9D8F 100%)",
              boxShadow: "0 8px 24px rgba(0,98,143,0.18)",
            }}
            data-testid="rec-executive-summary"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} color="white" />
              <span
                className="text-[10px] font-bold uppercase tracking-wider"
                style={{ color: "rgba(255,255,255,0.85)" }}
              >
                Resumen ejecutivo
              </span>
            </div>
            <p
              className="text-xs leading-relaxed"
              style={{ color: "rgba(255,255,255,0.95)" }}
            >
              {summary.resumen_ejecutivo}
            </p>
          </div>
        )}

        {/* ═══ Alert Banner ═══ */}
        {summary.alertas_y_riesgos && (
          <div
            className="we-card p-4 mb-3"
            style={{ borderLeft: `4px solid ${riskColor(riskLevel)}` }}
            data-testid="rec-alerts"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle size={14} color={riskColor(riskLevel)} />
                <span
                  className="text-xs font-bold"
                  style={{ color: "#2D2A24" }}
                >
                  Alertas y riesgos
                </span>
              </div>
              {riskLevel && (
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-md uppercase"
                  style={{
                    background: `${riskColor(riskLevel)}15`,
                    color: riskColor(riskLevel),
                  }}
                  data-testid="rec-alert-level"
                >
                  {riskLevel}
                </span>
              )}
            </div>
            {summary.alertas_y_riesgos.motivo && (
              <p
                className="text-[11px] leading-relaxed mb-2"
                style={{ color: "#2D2A24" }}
              >
                {summary.alertas_y_riesgos.motivo}
              </p>
            )}
            {summary.alertas_y_riesgos.accion_requerida && (
              <AlertBanner variant="warning" testId="rec-alert-action">
                <strong>Acción requerida:</strong>{" "}
                {summary.alertas_y_riesgos.accion_requerida}
              </AlertBanner>
            )}
          </div>
        )}

        {/* ═══ Master Plan Today ═══ */}
        {summary.plan_maestro_hoy && (
          <div className="we-card p-4 mb-3" data-testid="rec-master-plan">
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: "#FFF3DC" }}
              >
                <Target size={16} color="#F9A01B" />
              </div>
              <div>
                <p className="text-sm font-bold" style={{ color: "#2D2A24" }}>
                  Plan maestro de hoy
                </p>
                <p className="text-[10px]" style={{ color: "#71644C" }}>
                  Lo más importante para mover la aguja hoy
                </p>
              </div>
            </div>

            {summary.plan_maestro_hoy.prioridad_numero_1 && (
              <div
                className="rounded-xl p-3 mb-3"
                style={{ background: "#FFF3DC" }}
              >
                <p
                  className="text-[10px] font-bold uppercase tracking-wide mb-1"
                  style={{ color: "#F9A01B" }}
                >
                  Prioridad #1
                </p>
                <p
                  className="text-[12px] font-semibold leading-snug"
                  style={{ color: "#2D2A24" }}
                >
                  {summary.plan_maestro_hoy.prioridad_numero_1}
                </p>
              </div>
            )}

            {summary.plan_maestro_hoy.micro_intervenciones?.filter(Boolean)?.length > 0 && (
              <div className="mb-3">
                <p
                  className="text-[10px] font-bold uppercase tracking-wide mb-2"
                  style={{ color: "#71644C" }}
                >
                  Micro-intervenciones
                </p>
                {summary.plan_maestro_hoy.micro_intervenciones.filter(Boolean).map((m, i) => (
                  <div key={i} className="flex items-start gap-1.5 mb-1.5">
                    <CheckCircle
                      size={11}
                      color="#157346"
                      className="shrink-0 mt-0.5"
                    />
                    <span
                      className="text-[11px] leading-snug"
                      style={{ color: "#2D2A24" }}
                    >
                      {m}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {summary.plan_maestro_hoy.coherencia_entre_pilares && (
              <p
                className="text-[11px] leading-relaxed italic"
                style={{ color: "#71644C" }}
              >
                {summary.plan_maestro_hoy.coherencia_entre_pilares}
              </p>
            )}
          </div>
        )}

        {/* ═══ Pillar: Physical ═══ */}
        {physical && (
          <PhysicalSection physical={physical} testId="rec-physical" />
        )}

        {/* ═══ Pillar: Nutrition ═══ */}
        {nutrition && (
          <NutritionSection nutrition={nutrition} testId="rec-nutrition" />
        )}

        {/* ═══ Pillar: Emotional ═══ */}
        {emotional && (
          <EmotionalSection emotional={emotional} testId="rec-emotional" />
        )}

        {/* ═══ Motivational close ═══ */}
        {summary.motivacion_y_logros && (
          <div
            className="rounded-2xl p-5 mb-4 text-center"
            style={{
              background:
                "linear-gradient(135deg, #00628F 0%, #0080A0 50%, #2A9D8F 100%)",
              boxShadow: "0 8px 30px rgba(0,98,143,0.20)",
            }}
            data-testid="rec-motivational"
          >
            {summary.motivacion_y_logros.reto_general_semanal && (
              <div
                className="rounded-xl p-3 mb-3"
                style={{ background: "rgba(255,255,255,0.15)" }}
              >
                <p
                  className="text-[10px] font-bold uppercase tracking-wider mb-1"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  Reto de la semana
                </p>
                <p
                  className="text-xs font-bold leading-snug"
                  style={{ color: "white" }}
                >
                  {summary.motivacion_y_logros.reto_general_semanal}
                </p>
              </div>
            )}
            {summary.motivacion_y_logros.mensaje_coaching && (
              <p
                className="text-xs leading-relaxed mb-4"
                style={{ color: "rgba(255,255,255,0.9)" }}
              >
                {summary.motivacion_y_logros.mensaje_coaching}
              </p>
            )}
            <button
              onClick={() => navigate("/dashboard")}
              className="cursor-pointer active:scale-[0.97] transition-transform"
              style={{
                padding: "10px 24px",
                borderRadius: 12,
                border: "2px solid rgba(255,255,255,0.3)",
                background: "rgba(255,255,255,0.15)",
                color: "white",
                fontSize: 13,
                fontWeight: 700,
              }}
              data-testid="rec-back-dashboard-btn"
            >
              Volver al dashboard
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-4" data-testid="rec-footer">
          <p
            className="text-[10px] leading-relaxed"
            style={{ color: "#B5A998" }}
          >
            Estas recomendaciones son generadas por IA y son orientativas. No
            reemplazan una consulta médica profesional. Consulta a tu médico
            antes de iniciar cualquier programa de ejercicio o cambio
            alimentario.
          </p>
          {generatedAt && (
            <p className="text-[9px] mt-1" style={{ color: "#B5A998" }}>
              Generado el{" "}
              {new Date(generatedAt).toLocaleString("es-CO", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PHYSICAL pillar
// ═══════════════════════════════════════════════════════════
function PhysicalSection({ physical, testId }) {
  const cls = physical.classification || {};
  const fases = physical.fases || {};
  const verticales = physical.verticales || {};
  const gam = physical.gamificacion || {};

  const FASE_KEYS = [
    { k: "adaptacion", label: "Adaptación" },
    { k: "progresion", label: "Progresión" },
    { k: "consolidacion", label: "Consolidación" },
  ];

  return (
    <div className="mb-3" data-testid={testId}>
      <CollapsibleCard
        title="Pilar Físico"
        subtitle={cls.nivel_riesgo ? `Riesgo ${cls.nivel_riesgo}` : "Tu plan físico"}
        color="#F9A01B"
        icon={Activity}
        badge={cls.Status_ID}
        badgeColor="#F9A01B"
        testId={`${testId}-card`}
      >
        <div className="mt-2 space-y-3">
          {cls.condicion_fisica && (
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-wide mb-1"
                style={{ color: "#71644C" }}
              >
                Diagnóstico
              </p>
              <p
                className="text-[11px] leading-relaxed"
                style={{ color: "#2D2A24" }}
              >
                {cls.condicion_fisica}
              </p>
            </div>
          )}

          {physical.planSummary && (
            <AlertBanner variant="warning" testId={`${testId}-summary`}>
              <strong>Plan:</strong> {physical.planSummary}
            </AlertBanner>
          )}

          {/* Fases */}
          {Object.keys(fases).length > 0 && (
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-wide mb-2"
                style={{ color: "#71644C" }}
              >
                Fases del programa
              </p>
              <div className="space-y-2">
                {FASE_KEYS.filter((f) => fases[f.k]).map((f) => (
                  <PhaseCard
                    key={f.k}
                    label={f.label}
                    fase={fases[f.k]}
                    testId={`${testId}-fase-${f.k}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Verticales */}
          {(verticales.fuerza || verticales.aerobico || verticales.flexibilidad) && (
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-wide mb-2"
                style={{ color: "#71644C" }}
              >
                Tipos de ejercicio sugeridos
              </p>
              {verticales.fuerza && (
                <VerticalRow label="Fuerza" text={verticales.fuerza} />
              )}
              {verticales.aerobico && (
                <VerticalRow label="Aeróbico" text={verticales.aerobico} />
              )}
              {verticales.flexibilidad && (
                <VerticalRow
                  label="Flexibilidad"
                  text={verticales.flexibilidad}
                />
              )}
            </div>
          )}

          {/* Gamificación */}
          {(gam.reto_semanal || gam.mensaje_motivacional) && (
            <div
              className="rounded-xl p-3"
              style={{ background: "#FFF3DC" }}
              data-testid={`${testId}-gamificacion`}
            >
              {gam.reto_semanal && (
                <>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Flame size={11} color="#F9A01B" />
                    <span
                      className="text-[10px] font-bold uppercase tracking-wide"
                      style={{ color: "#F9A01B" }}
                    >
                      Reto semanal
                    </span>
                  </div>
                  <p
                    className="text-[11px] font-semibold leading-snug mb-2"
                    style={{ color: "#2D2A24" }}
                  >
                    {gam.reto_semanal}
                  </p>
                </>
              )}
              {gam.mensaje_motivacional && (
                <p
                  className="text-[11px] leading-relaxed italic"
                  style={{ color: "#71644C" }}
                >
                  {gam.mensaje_motivacional}
                </p>
              )}
            </div>
          )}
        </div>
      </CollapsibleCard>
    </div>
  );
}

function PhaseCard({ label, fase, testId }) {
  return (
    <div
      className="rounded-xl p-3"
      style={{ background: "#FCFAEE", border: "1px solid #E2D9CB" }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-bold" style={{ color: "#2D2A24" }}>
          {label}
        </span>
        {fase.semanas && (
          <span
            className="text-[9px] font-semibold px-2 py-0.5 rounded-md"
            style={{ background: "#FFF3DC", color: "#F9A01B" }}
          >
            Semanas {fase.semanas}
          </span>
        )}
      </div>
      {fase.objetivo && (
        <p
          className="text-[10.5px] leading-relaxed mb-2"
          style={{ color: "#71644C" }}
        >
          {fase.objetivo}
        </p>
      )}
      {fase.ejercicios?.filter(ej => ej && (ej.tipo || ej.actividad))?.length > 0 && (
        <div className="space-y-1.5">
          {fase.ejercicios.filter(ej => ej && (ej.tipo || ej.actividad)).map((ej, i) => (
            <div
              key={i}
              className="rounded-lg p-2"
              style={{ background: "white", border: "1px solid #E2D9CB" }}
            >
              <div className="flex items-center justify-between mb-0.5">
                <span
                  className="text-[10px] font-bold"
                  style={{ color: "#F9A01B" }}
                >
                  {ej.tipo}
                </span>
                {ej.frecuencia && (
                  <span
                    className="text-[9px]"
                    style={{ color: "#71644C" }}
                  >
                    {ej.frecuencia}
                  </span>
                )}
              </div>
              {ej.actividad && (
                <p
                  className="text-[10.5px] leading-snug"
                  style={{ color: "#2D2A24" }}
                >
                  {ej.actividad}
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-1">
                {ej.duracion && (
                  <Pill label="Duración" value={ej.duracion} />
                )}
                {ej.series && <Pill label="Series" value={ej.series} />}
                {ej.repeticiones && (
                  <Pill label="Reps" value={ej.repeticiones} />
                )}
                {ej.intensidad && (
                  <Pill label="Intensidad" value={ej.intensidad} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VerticalRow({ label, text }) {
  return (
    <div className="mb-1.5">
      <span
        className="text-[10px] font-bold mr-1"
        style={{ color: "#F9A01B" }}
      >
        {label}:
      </span>
      <span className="text-[10.5px]" style={{ color: "#2D2A24" }}>
        {text}
      </span>
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <span
      className="text-[9px] px-1.5 py-0.5 rounded-md"
      style={{ background: "#E3F0F5", color: "#00628F" }}
    >
      <strong>{label}:</strong> {value}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════
// NUTRITION pillar
// ═══════════════════════════════════════════════════════════
function NutritionSection({ nutrition, testId }) {
  const evalF = nutrition.evaluacion_funcional || {};
  const prog = nutrition.programa_progresivo || {};
  const plan = nutrition.plan_diario || {};
  const adh = nutrition.estrategia_adherencia || {};
  const edu = nutrition.educacion_microlearning || {};

  return (
    <div className="mb-3" data-testid={testId}>
      <CollapsibleCard
        title="Pilar Nutricional"
        subtitle={evalF.clasificacion_dieta || "Tu plan alimentario"}
        color="#157346"
        icon={Apple}
        badge={evalF.clasificacion_dieta}
        badgeColor="#157346"
        testId={`${testId}-card`}
      >
        <div className="mt-2 space-y-3">
          {evalF.diagnostico && (
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-wide mb-1"
                style={{ color: "#71644C" }}
              >
                Diagnóstico
              </p>
              <p
                className="text-[11px] leading-relaxed"
                style={{ color: "#2D2A24" }}
              >
                {evalF.diagnostico}
              </p>
            </div>
          )}

          {evalF.puntos_ciegos?.filter(Boolean)?.length > 0 && (
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-wide mb-1.5"
                style={{ color: "#C0453A" }}
              >
                Puntos ciegos
              </p>
              {evalF.puntos_ciegos.filter(Boolean).map((p, i) => (
                <div key={i} className="flex items-start gap-1.5 mb-1.5">
                  <AlertTriangle
                    size={10}
                    color="#C0453A"
                    className="shrink-0 mt-0.5"
                  />
                  <span
                    className="text-[11px] leading-snug"
                    style={{ color: "#2D2A24" }}
                  >
                    {p}
                  </span>
                </div>
              ))}
            </div>
          )}

          {prog.objetivos_fase && (
            <AlertBanner variant="success" testId={`${testId}-prog`}>
              {prog.fase_actual && <strong>{prog.fase_actual}: </strong>}
              {prog.objetivos_fase}
            </AlertBanner>
          )}

          {prog.intervenciones_clave?.filter(Boolean)?.length > 0 && (
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-wide mb-1.5"
                style={{ color: "#157346" }}
              >
                Intervenciones clave
              </p>
              {prog.intervenciones_clave.filter(Boolean).map((it, i) => (
                <div key={i} className="flex items-start gap-1.5 mb-1.5">
                  <CheckCircle
                    size={10}
                    color="#157346"
                    className="shrink-0 mt-0.5"
                  />
                  <span
                    className="text-[11px] leading-snug"
                    style={{ color: "#2D2A24" }}
                  >
                    {it}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Plan diario */}
          {(plan.desayuno || plan.almuerzo || plan.cena) && (
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-wide mb-2"
                style={{ color: "#71644C" }}
              >
                Plan diario sugerido
              </p>
              <div className="space-y-2">
                {plan.desayuno && (
                  <MealCard label="Desayuno" meal={plan.desayuno} />
                )}
                {plan.almuerzo && (
                  <MealCard label="Almuerzo" meal={plan.almuerzo} />
                )}
                {plan.cena && <MealCard label="Cena" meal={plan.cena} />}
                {plan.power_snacks?.filter(s => s && s.alimentos)?.length > 0 && (
                  <div
                    className="rounded-xl p-3"
                    style={{
                      background: "#FCFAEE",
                      border: "1px solid #E2D9CB",
                    }}
                  >
                    <p
                      className="text-[10px] font-bold uppercase tracking-wide mb-1.5"
                      style={{ color: "#157346" }}
                    >
                      Power snacks
                    </p>
                    {plan.power_snacks.filter(s => s && s.alimentos).map((s, i) => (
                      <div key={i} className="mb-1.5 last:mb-0">
                        <p
                          className="text-[10.5px] font-semibold"
                          style={{ color: "#2D2A24" }}
                        >
                          • {s.alimentos}
                        </p>
                        {s.por_que_funciona && (
                          <p
                            className="text-[10px] leading-snug ml-2"
                            style={{ color: "#71644C" }}
                          >
                            {s.por_que_funciona}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Adherencia */}
          {(adh.reto_semanal || adh.metas_diarias) && (
            <div
              className="rounded-xl p-3"
              style={{ background: "#E2F0E8" }}
              data-testid={`${testId}-adherencia`}
            >
              {adh.reto_semanal && (
                <>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Flame size={11} color="#157346" />
                    <span
                      className="text-[10px] font-bold uppercase tracking-wide"
                      style={{ color: "#157346" }}
                    >
                      Reto semanal
                    </span>
                  </div>
                  <p
                    className="text-[11px] font-semibold leading-snug mb-2"
                    style={{ color: "#2D2A24" }}
                  >
                    {adh.reto_semanal}
                  </p>
                </>
              )}
              {adh.metas_diarias?.filter(Boolean)?.length > 0 && (
                <div>
                  <p
                    className="text-[10px] font-bold uppercase tracking-wide mb-1"
                    style={{ color: "#157346" }}
                  >
                    Metas diarias
                  </p>
                  {adh.metas_diarias.filter(Boolean).map((m, i) => (
                    <p
                      key={i}
                      className="text-[10.5px] leading-snug mb-0.5"
                      style={{ color: "#2D2A24" }}
                    >
                      • {m}
                    </p>
                  ))}
                </div>
              )}
              {adh.mensaje_refuerzo && (
                <p
                  className="text-[10.5px] leading-relaxed italic mt-2"
                  style={{ color: "#71644C" }}
                >
                  {adh.mensaje_refuerzo}
                </p>
              )}
            </div>
          )}

          {/* Educación */}
          {edu.capsula_conocimiento && (
            <div data-testid={`${testId}-educacion`}>
              <div className="flex items-center gap-1.5 mb-1">
                <BookOpen size={11} color="#00628F" />
                <span
                  className="text-[10px] font-bold uppercase tracking-wide"
                  style={{ color: "#00628F" }}
                >
                  Cápsula de conocimiento
                </span>
              </div>
              <p
                className="text-[10.5px] leading-relaxed"
                style={{ color: "#2D2A24" }}
              >
                {edu.capsula_conocimiento}
              </p>
            </div>
          )}
        </div>
      </CollapsibleCard>
    </div>
  );
}

function MealCard({ label, meal }) {
  return (
    <div
      className="rounded-xl p-3"
      style={{ background: "white", border: "1px solid #E2D9CB" }}
    >
      <p
        className="text-[10px] font-bold uppercase tracking-wide mb-1"
        style={{ color: "#157346" }}
      >
        {label}
      </p>
      {meal.alimentos && (
        <p
          className="text-[11px] font-semibold leading-snug mb-1"
          style={{ color: "#2D2A24" }}
        >
          {meal.alimentos}
        </p>
      )}
      {meal.preparacion && (
        <p
          className="text-[10px] leading-snug mb-1"
          style={{ color: "#71644C" }}
        >
          <strong>Preparación:</strong> {meal.preparacion}
        </p>
      )}
      {meal.por_que_funciona && (
        <p
          className="text-[10px] leading-snug italic"
          style={{ color: "#71644C" }}
        >
          {meal.por_que_funciona}
        </p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// EMOTIONAL pillar
// ═══════════════════════════════════════════════════════════
function EmotionalSection({ emotional, testId }) {
  const evalSna = emotional.evaluacion_sna || {};
  const planP = emotional.plan_progresivo || {};
  const bio = emotional.intervenciones_biohacking || {};
  const circ = emotional.entorno_circadiano || {};
  const edu = emotional.educacion_neurociencia || {};
  const tecnica = planP.tecnica_rescate || {};
  // some payloads use Spanish keys with tilde: "mañana_activacion"
  const manana = bio["mañana_activacion"] || bio.manana_activacion;
  const noche =
    bio["noche_rituo_desactivacion"] ||
    bio["noche_ritual_desactivacion"] ||
    bio.noche_ritual_desactivacion;

  return (
    <div className="mb-3" data-testid={testId}>
      <CollapsibleCard
        title="Pilar Emocional"
        subtitle={evalSna.estado_actual || "Tu plan de bienestar mental"}
        color="#883476"
        icon={Brain}
        testId={`${testId}-card`}
      >
        <div className="mt-2 space-y-3">
          {evalSna.estado_actual && (
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-wide mb-1"
                style={{ color: "#71644C" }}
              >
                Estado actual
              </p>
              <p
                className="text-[11px] leading-relaxed"
                style={{ color: "#2D2A24" }}
              >
                {evalSna.estado_actual}
              </p>
            </div>
          )}

          {evalSna.analisis_fisiologico && (
            <AlertBanner variant="info" testId={`${testId}-analisis`}>
              {evalSna.analisis_fisiologico}
            </AlertBanner>
          )}

          {evalSna.alertas_bienestar?.filter(Boolean)?.length > 0 && (
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-wide mb-1.5"
                style={{ color: "#C0453A" }}
              >
                Alertas de bienestar
              </p>
              {evalSna.alertas_bienestar.filter(Boolean).map((a, i) => (
                <div key={i} className="flex items-start gap-1.5 mb-1.5">
                  <AlertTriangle
                    size={10}
                    color="#C0453A"
                    className="shrink-0 mt-0.5"
                  />
                  <span
                    className="text-[11px] leading-snug"
                    style={{ color: "#2D2A24" }}
                  >
                    {a}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Técnica de rescate */}
          {tecnica.nombre && (
            <div
              className="rounded-xl p-3"
              style={{
                background: "#F4E8F1",
                border: "1px solid rgba(136,52,118,0.15)",
              }}
              data-testid={`${testId}-tecnica`}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Lightbulb size={11} color="#883476" />
                <span
                  className="text-[10px] font-bold uppercase tracking-wide"
                  style={{ color: "#883476" }}
                >
                  Técnica de rescate
                </span>
              </div>
              <p
                className="text-[11px] font-semibold mb-1"
                style={{ color: "#2D2A24" }}
              >
                {tecnica.nombre}
              </p>
              {tecnica.instrucciones_paso_a_paso && (
                <p
                  className="text-[10.5px] leading-relaxed"
                  style={{ color: "#71644C" }}
                >
                  {tecnica.instrucciones_paso_a_paso}
                </p>
              )}
            </div>
          )}

          {/* Biohacking */}
          {(manana || bio.trabajo_coherencia || noche) && (
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-wide mb-2"
                style={{ color: "#71644C" }}
              >
                Intervenciones del día
              </p>
              <div className="space-y-2">
                {manana && (
                  <BioRow
                    icon={Sun}
                    color="#F9A01B"
                    label="Mañana · activación"
                    text={manana}
                  />
                )}
                {bio.trabajo_coherencia && (
                  <BioRow
                    icon={Activity}
                    color="#00628F"
                    label="Trabajo · coherencia"
                    text={bio.trabajo_coherencia}
                  />
                )}
                {noche && (
                  <BioRow
                    icon={Moon}
                    color="#883476"
                    label="Noche · desactivación"
                    text={noche}
                  />
                )}
              </div>
            </div>
          )}

          {/* Entorno circadiano */}
          {(circ.sugerencia_luz || circ.control_ambiental) && (
            <div data-testid={`${testId}-circadiano`}>
              <p
                className="text-[10px] font-bold uppercase tracking-wide mb-1.5"
                style={{ color: "#71644C" }}
              >
                Entorno circadiano
              </p>
              {circ.sugerencia_luz && (
                <p
                  className="text-[10.5px] leading-relaxed mb-1"
                  style={{ color: "#2D2A24" }}
                >
                  <strong style={{ color: "#883476" }}>Luz:</strong>{" "}
                  {circ.sugerencia_luz}
                </p>
              )}
              {circ.control_ambiental && (
                <p
                  className="text-[10.5px] leading-relaxed"
                  style={{ color: "#2D2A24" }}
                >
                  <strong style={{ color: "#883476" }}>Ambiente:</strong>{" "}
                  {circ.control_ambiental}
                </p>
              )}
            </div>
          )}

          {/* Educación */}
          {edu.capsula_microlearning && (
            <div data-testid={`${testId}-educacion`}>
              <div className="flex items-center gap-1.5 mb-1">
                <BookOpen size={11} color="#00628F" />
                <span
                  className="text-[10px] font-bold uppercase tracking-wide"
                  style={{ color: "#00628F" }}
                >
                  Neurociencia para ti
                </span>
              </div>
              <p
                className="text-[10.5px] leading-relaxed"
                style={{ color: "#2D2A24" }}
              >
                {edu.capsula_microlearning}
              </p>
            </div>
          )}
        </div>
      </CollapsibleCard>
    </div>
  );
}

function BioRow({ icon: Icon, color, label, text }) {
  return (
    <div
      className="rounded-xl p-2.5 flex gap-2"
      style={{ background: "#FCFAEE", border: "1px solid #E2D9CB" }}
    >
      <div
        className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: `${color}15` }}
      >
        <Icon size={13} color={color} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold mb-0.5" style={{ color }}>
          {label}
        </p>
        <p
          className="text-[10.5px] leading-snug"
          style={{ color: "#2D2A24" }}
        >
          {text}
        </p>
      </div>
    </div>
  );
}
